import useBEOImportJobsStore from '../state/beoImportJobs';
import useActiveLocationStore from '../state/locationScope';

export type SessionCleanup = () => void | Promise<void>;

const cleanupRegistry = new Set<SessionCleanup>();

export function registerSessionCleanup(cleanup: SessionCleanup): () => void {
  cleanupRegistry.add(cleanup);
  return () => cleanupRegistry.delete(cleanup);
}

export async function runRegisteredSessionCleanup(): Promise<void> {
  const results = await Promise.allSettled(
    [...cleanupRegistry].map(async (cleanup) => cleanup()),
  );
  for (const result of results) {
    if (result.status === 'rejected') {
      // eslint-disable-next-line no-console
      console.error('[session-cleanup] registered cleanup failed', result.reason);
    }
  }
}

const LOCAL_STORAGE_KEYS = new Set([
  'itemsPanelSkuCache',
  'ita.chat.lastConversation',
  'ita.chat.lastTab',
  'ita.chat.closedConversations',
  'last_event_id',
  'mtg-vault.scan-session.v1',
  'aiProgress.currentJobId',
  'aiProgress.modelCache',
  'admin:site',
]);

const LOCAL_STORAGE_PREFIXES = [
  'chat:lastRead:',
  'orderChat:lastRead:',
  'roomPhotoPrompts:',
  'aiProgress.jobs.',
  'mock:order:',
  'mock:quote:',
] as const;

const SESSION_STORAGE_KEYS = new Set([
  'beoImport.jobs',
  'vrr.active',
  'ita.app.presenceSessionId',
  'chat:dismissed-convs',
  'notificationBell.quietJobs',
]);

const SESSION_STORAGE_PREFIXES = [
  'price-check-event-draft:',
  'sess:event:',
  'ita.notifications.dismissed:',
] as const;

function removeKnownKeys(
  storage: Storage,
  exactKeys: ReadonlySet<string>,
  prefixes: readonly string[],
): void {
  const keys = Array.from({ length: storage.length }, (_unused, index) => storage.key(index))
    .filter((key): key is string => Boolean(key));
  for (const key of keys) {
    if (exactKeys.has(key) || prefixes.some((prefix) => key.startsWith(prefix))) {
      storage.removeItem(key);
    }
  }
}

export function clearSessionOwnedBrowserStorage(authStorageKey?: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    removeKnownKeys(window.localStorage, LOCAL_STORAGE_KEYS, LOCAL_STORAGE_PREFIXES);
    if (authStorageKey) window.localStorage.removeItem(authStorageKey);
  } catch {
    // Best effort in privacy-restricted browsers.
  }
  try {
    removeKnownKeys(window.sessionStorage, SESSION_STORAGE_KEYS, SESSION_STORAGE_PREFIXES);
  } catch {
    // Best effort in privacy-restricted browsers.
  }
}

export async function clearPrincipalOwnedState(authStorageKey?: string | null): Promise<void> {
  useActiveLocationStore.getState().reset();
  useBEOImportJobsStore.getState().reset();
  clearSessionOwnedBrowserStorage(authStorageKey);
  await runRegisteredSessionCleanup();
}
